import React, { useEffect } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import supabase from '@shared/api/supabase';
import { createImmerStore } from '@/state/createImmerStore';

type AuthState = {
  initialized: boolean;
  loading: boolean;
  session: Session | null;
  user: User | null;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
};

const useAuthStore = createImmerStore<AuthState>((set) => ({
  initialized: false,
  loading: true,
  session: null,
  user: null,
  error: null,

  async signIn(email, password) {
    set((s) => { s.loading = true; s.error = null; });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    set((s) => {
      s.loading = false;
      s.error = error?.message ?? null;
      s.session = data?.session ?? null;
      s.user = data?.user ?? null;
    });
  },

  async signOut() {
    set((s) => { s.loading = true; s.error = null; });
    const { error } = await supabase.auth.signOut();
    set((s) => {
      s.loading = false;
      s.error = error?.message ?? null;
      s.session = null;
      s.user = null;
    });
  },

  async requestPasswordReset(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${location.origin}/auth/update-password`,
    });
    if (error) set((s) => { s.error = error.message; });
  },

  async updatePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    set((s) => {
      s.error = error?.message ?? null;
      if (data?.user) s.user = (data.user as User);
    });
    // Refresh session explicitly (updateUser doesn't return session in typings)
    const { data: sess } = await supabase.auth.getSession();
    set((s) => {
      s.session = (sess?.session as Session) ?? null;
    });
  },
}));

export function AuthProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // Never hang indefinitely: race Supabase getSession with a short timeout.
        const result = await Promise.race<
          { data: { session: Session | null } } | undefined
        >([
          supabase.auth.getSession(),
          new Promise((resolve) => setTimeout(() => resolve(undefined), 2000)) as any,
        ]);
        const session = result?.data?.session ?? null;
        if (!mounted) return;
        useAuthStore.setState((s) => {
          s.initialized = true;
          s.loading = false;
          s.session = session as Session | null;
          s.user = (session?.user as User) ?? null;
          s.error = null;
        });
      } catch (e: any) {
        if (!mounted) return;
        // Fail open to let app route to /auth/login instead of spinning forever.
        useAuthStore.setState((s) => {
          s.initialized = true;
          s.loading = false;
          s.session = null;
          s.user = null;
          s.error = e?.message ?? String(e);
        });
      }
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      useAuthStore.setState((s) => {
        s.initialized = true;
        s.loading = false;
        s.session = session as Session | null;
        s.user = (session?.user as User) ?? null;
      });
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return <>{children}</>;
}

export function useAuth() {
  return useAuthStore();
}
